import React, { FC, useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  CardActionArea,
  CircularProgress,
  FormGroup,
  IconButton,
  Modal,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';

import {
  RotateLeftOutlined,
  RotateRightOutlined,
  SaveOutlined,
  Slideshow,
  BorderColor,
} from '@mui/icons-material';
import CachedIcon from '@mui/icons-material/Cached';
import { useAIRenderStyles } from './styles';
import { StyledSideTab, StyledSideTabs } from '../StyledTabs';
import { useSessionContext } from '../../contexts/SessionContext';
import { IImage } from '../../global/interfaces';

import {
  CloseOutlined,
  FileDownloadOutlined,
  FileUploadOutlined,
} from '@mui/icons-material';

import useCopilot from '../../hooks/useCopilot';
import { axiosRequest } from '../../utils/axiosRequest';
import { useBookingsContext } from '../../contexts/BookingsContext';
import useSession from '../../hooks/useSession';
import useImageUpload from '../../hooks/useImageUpload';
import CanvasImage from './CanvasImage';
import GeneratedImage from './GeneratedImage';
import SaveIcon from '../../icons/SaveIcon';
import { StyledImageOverlay } from '../../pages/AIDocumentEditor/DocumentWorkspace/DocumentRenderer/styles';

const cropAndResizeCanvas = (
  sourceCanvas: HTMLCanvasElement,
): HTMLCanvasElement => {
  const isLandscape = sourceCanvas.width > sourceCanvas.height;
  const targetSize = isLandscape
    ? { width: 1792, height: 1024 }
    : { width: 1024, height: 1792 };

  const ar = isLandscape
    ? sourceCanvas.width / sourceCanvas.height
    : sourceCanvas.height / sourceCanvas.width;

  if (ar > 1792 / 1024) {
    const minDim = Math.min(sourceCanvas.width, sourceCanvas.height);
    if (minDim < 1024) {
      if (isLandscape) {
        targetSize.width *= sourceCanvas.height / 1024;
        targetSize.height = minDim;
      } else {
        targetSize.height *= sourceCanvas.width / 1024;
        targetSize.width = minDim;
      }
    }
  } else {
    const maxDim = Math.max(sourceCanvas.width, sourceCanvas.height);
    if (maxDim < 1024) {
      if (!isLandscape) {
        targetSize.width *= sourceCanvas.height / 1024;
        targetSize.height = maxDim;
      } else {
        targetSize.height *= sourceCanvas.width / 1024;
        targetSize.width = maxDim;
      }
    }
  }

  const offsetX = (sourceCanvas.width - targetSize.width) / 2;
  const offsetY = (sourceCanvas.height - targetSize.height) / 2;

  // Create target canvas
  const targetCanvas = document.createElement('canvas');
  targetCanvas.width = targetSize.width;
  targetCanvas.height = targetSize.height;

  const ctx = targetCanvas.getContext('2d', { alpha: false });
  if (!ctx) throw new Error('2D context not available');
  ctx.clearRect(0, 0, targetSize.width, targetSize.height);

  // Draw cropped and resized image onto new canvas
  ctx.drawImage(
    sourceCanvas,
    offsetX,
    offsetY,
    targetSize.width,
    targetSize.height, // source crop
    0,
    0,
    targetSize.width,
    targetSize.height, // target size
  );

  return targetCanvas;
};

interface AIRenderProps {
  viewTitle: string;
}

const AIRenderer: FC<AIRenderProps> = ({ viewTitle }) => {
  const styles = useAIRenderStyles();
  const { selectedBooking } = useBookingsContext();
  const { uploadImage, deleteImage } = useSession();
  const {
    client,
    images,
    imagesMapRef,
    appendImage,
    selectedImage,
    setSelectedImage,
  } = useSessionContext();
  const { getImageAsBase64 } = useCopilot();
  const [previewImage, setPreviewImage] = useState<IImage | null>(null);
  const [previewImageElement, setPreviewImageElement] =
    useState<HTMLImageElement | null>(null);
  const [isImageLoading, setIsImageLoading] = useState<boolean>(false);
  const [strokesLayer, setStrokesLayer] = useState<number>(0);
  const [rotation, setRotation] = useState<number>(0);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  const {
    selectedLocalFile,
    imageUrl,
    fileInputRef,
    imageRef,
    dimensions,
    handleFileChange,
    setSelectedLocalFile,
    handleUpload,
    isUploading,
  } = useImageUpload();

  useEffect(() => {
    if (!previewImage) {
      setPreviewImageElement(null);
      return;
    }

    if (previewImage.src) console.log(`setSelectedImage: ${previewImage.src}`);

    setIsImageLoading(true);

    var img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      console.log(
        `::::image is loaded img.width: ${img.width} x  img.height: ${img.height}`,
      );
      setIsImageLoading(false);
      setPreviewImageElement(img);
    };

    img.onerror = () => {
      setIsImageLoading(false);
      alert('Error occured while loading selected image !');
    };

    setStrokesLayer(!previewImage.layer ? 0 : previewImage.layer);
    if (previewImage.src) img.src = previewImage.src;
    else {
      setIsImageLoading(false);
      img.width = previewImage.width;
      img.height = previewImage.height;
    }
  }, [previewImage]);

  const [prompt, setPrompt] = useState<string>('');
  const [generatedImage, setGeneratedImage] = useState<{
    src: string;
    width: number;
    height: number;
  } | null>(null);
  const [isGeneratingImage, setIsGeneratingImage] = useState<boolean>(false);

  const generateImage = async () => {
    if (!previewImage || !prompt) return;

    try {
      setIsGeneratingImage(true);

      console.log('selectedImage.src:', previewImage.src);

      //await new Promise((resolve) => setTimeout(resolve, 1000));

      const canvas =
        canvasRef?.current && cropAndResizeCanvas(canvasRef.current);

      console.log('canvas:', canvas);
      if (!canvas) {
        setIsGeneratingImage(false);
        alert('Canvas is not found');
        return;
      }

      const blob = await new Promise<Blob | null>((resolve) =>
        canvas.toBlob(resolve, 'image/png'),
      );

      console.log('blob:', blob);
      if (!blob) return;

      const formData = new FormData();
      formData.append('image', blob, 'input.png');
      formData.append('prompt', prompt);
      formData.append('size', '512x512');
      formData.append('model', 'dall-e-2');

      const size = 512;
      const rimWidth = 10;

      const maskCanvas = document.createElement('canvas');
      maskCanvas.width = size;
      maskCanvas.height = size;

      const ctx = maskCanvas.getContext('2d');
      if (!ctx) throw new Error('2D context not available');

      ctx.clearRect(0, 0, 512, 512);

      ctx.fillStyle = 'rgba(0, 0, 0, 255)'; // black + full alpha
      ctx.fillRect(300, 300, 112, 112);

      const maskBlob = await new Promise<Blob | null>((resolve) =>
        maskCanvas.toBlob(resolve, 'image/png'),
      );

      if (maskBlob) {
        formData.append('mask', maskBlob, 'mask.png');
      }

      const axios = axiosRequest();
      const response = await axios.post('ai/generate_image', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      console.log('::::response:', response);
      const data = response.data;
      if (data.status === 'success' && data.imageUrl) {
        setGeneratedImage({
          src: data.imageUrl,
          width: 512,
          height: 512,
        });
      }

      setIsGeneratingImage(false);
    } catch (error) {
      setIsGeneratingImage(false);
      console.error('Error generating image:', error);
    }
  };

  const downloadCroppedImage = async () => {
    if (!previewImage) return;

    try {
      const canvas =
        canvasRef?.current && cropAndResizeCanvas(canvasRef.current);

      if (!canvas) {
        alert('Canvas is not found');
        return;
      }

      // Create temporary link element
      const link = document.createElement('a');
      link.download = 'cropped-image.png';

      // Convert canvas to blob URL
      const blob = await new Promise<Blob | null>((resolve) =>
        canvas.toBlob(resolve, 'image/png'),
      );

      if (!blob) {
        alert('Failed to create image blob');
        return;
      }

      link.href = URL.createObjectURL(blob);

      // Programmatically click link to trigger download
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Clean up blob URL
      URL.revokeObjectURL(link.href);
    } catch (error) {
      console.error('Error downloading image:', error);
    }
  };

  return (
    <>
      <Modal
        open={!!generatedImage?.src}
        onClose={() => {}}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
        <Box
          sx={{
            backgroundColor: '#fff',
            padding: '2rem',
            borderRadius: '4px',
            maxWidth: '80vw',
            maxHeight: '80vh',
            width: '90%',
            height: '90%',
            display: 'flex',
            flexDirection: 'column',
          }}>
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: 'calc(100% - 2rem)',
            }}>
            <GeneratedImage
              imageUrl={generatedImage?.src}
              width={(generatedImage?.width || 0) * 2}
              height={generatedImage?.height}
            />
          </Box>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 2,
              height: '2rem',
            }}>
            <Button
              variant="contained"
              onClick={() => {
                // TODO: Add save logic
                setGeneratedImage(null);
              }}>
              Save
            </Button>
            <Button variant="outlined" onClick={() => setGeneratedImage(null)}>
              Close
            </Button>
          </Box>
        </Box>
      </Modal>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        multiple={false}
        onChange={handleFileChange}
        onClick={(e) => e.stopPropagation()}
        style={{ display: 'none' }}
      />
      <StyledSideTabs value={0} onChange={() => {}}>
        <StyledSideTab value={0} label={viewTitle} />
        <FormGroup row sx={styles.iconTray}>
          <Box sx={{ marginLeft: 'auto', display: 'flex' }}>
            {!selectedLocalFile && (
              <Tooltip title={'Upload Images'}>
                <IconButton
                  onClick={() => {
                    setPreviewImage(null);
                    setSelectedLocalFile(null);
                    fileInputRef.current?.click();
                  }}
                  disabled={!!selectedLocalFile}
                  sx={{
                    ...styles.trayButton,
                    marginLeft: 'auto',
                  }}>
                  <FileUploadOutlined sx={{ fontSize: '1.5rem' }} />
                </IconButton>
              </Tooltip>
            )}
            {selectedLocalFile && (
              <>
                <Tooltip title={'Upload Image'}>
                  <IconButton
                    key="upload-image"
                    disabled={isUploading}
                    sx={{
                      ...styles.trayButton,
                      marginLeft: 'auto',
                    }}
                    onClick={() =>
                      handleUpload({
                        selectedBooking,
                        client,
                        uploadImage,
                        appendImage,
                      })
                    }>
                    {isUploading ? (
                      <CircularProgress size={24} />
                    ) : (
                      <SaveIcon strokeWidth={2} sx={{ fontSize: '1.5rem' }} />
                    )}
                  </IconButton>
                </Tooltip>
                <Tooltip title={'Cancel'}>
                  <IconButton
                    key="menu-cancel-upload"
                    sx={styles.trayButton}
                    onClick={() => {
                      setSelectedLocalFile(null);
                      //setIsImageLoading(false);
                    }}>
                    <CloseOutlined />
                  </IconButton>
                </Tooltip>
              </>
            )}
            {previewImage && (
              <>
                {false && (
                  <Tooltip title="Generate Image">
                    <IconButton
                      onClick={(e) => {
                        e.stopPropagation();
                        if (!prompt) {
                          alert('Please enter a prompt');
                          return;
                        }
                        generateImage();
                      }}
                      sx={styles.trayButton}>
                      <CachedIcon />
                    </IconButton>
                  </Tooltip>
                )}

                <Tooltip title="Show to Virtual Visitor">
                  <IconButton
                    onClick={() => {
                      if (previewImage) {
                        setSelectedImage(selectedImage ? null : previewImage); //need to pass instructions for rotate....
                      }
                    }}
                    sx={{
                      ...styles.trayButton,
                      border: '1px solid #eee',
                      borderRadius: '.25rem',
                    }}>
                    <Slideshow />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Rotate Counter-Clockwise">
                  <IconButton
                    onClick={() => {
                      console.log(
                        'rotate counter-clockwise: ',
                        (rotation - 90) % 360,
                      );
                      setRotation((prev) => (prev - 90) % 360);
                    }}
                    sx={styles.trayButton}>
                    <RotateLeftOutlined />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Rotate Clockwise">
                  <IconButton
                    onClick={() => {
                      console.log('rotate clockwise: ', (rotation + 90) % 360);
                      setRotation((prev) => (prev + 90) % 360);
                    }}
                    sx={styles.trayButton}>
                    <RotateRightOutlined />
                  </IconButton>
                </Tooltip>

                <Tooltip title={'Download Cropped Image'}>
                  <IconButton
                    key="menu-download-image"
                    sx={styles.trayButton}
                    onClick={() => {
                      downloadCroppedImage();
                    }}>
                    <FileDownloadOutlined />
                  </IconButton>
                </Tooltip>
                <Tooltip title={'Cancel'}>
                  <IconButton
                    key="menu-upload-image"
                    sx={styles.trayButton}
                    onClick={() => {
                      setPreviewImage(null);
                    }}>
                    <CloseOutlined />
                  </IconButton>
                </Tooltip>
              </>
            )}
          </Box>
        </FormGroup>
      </StyledSideTabs>
      <Box
        sx={{
          height: 'calc(100% - 3rem)',
          backgroundColor: '#fff',
          minWidth: 0,
          width: '100%',
          maxWidth: '100%',
          //display: 'flex',
        }}
        onClick={(e) => {
          e.stopPropagation();
          setPreviewImage(null);
        }}>
        <Box
          sx={{
            margin: 'auto 2rem',
            height: '5rem',
            display: 'flex',
            alignItems: 'center',
          }}>
          <TextField
            fullWidth
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            placeholder="Enter prompt here..."
            variant="outlined"
            size="small"
            sx={{
              backgroundColor: '#fff',
              '& .MuiOutlinedInput-root': {
                borderRadius: '.25rem',
              },
            }}
          />
        </Box>

        <Box
          sx={{
            flex: 1,
            display: 'flex',
            height: 'calc(100% - 18.5rem)',
            padding: 0,
            margin: '1rem',
            justifyContent: 'center',
            alignItems: 'center',
            position: 'relative',
          }}>
          {isImageLoading || isGeneratingImage ? (
            <CircularProgress />
          ) : selectedLocalFile ? (
            <Box
              sx={{
                position: 'relative',
                margin: 'auto',
                objectFit: 'contain',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '1rem',
                '& img': {
                  maxWidth: '100%',
                  maxHeight: '100%',
                  objectFit: 'contain',
                  margin: 'auto',
                },
              }}>
              <img
                ref={imageRef}
                src={imageUrl}
                alt="Preview"
                style={{
                  maxWidth: '100%',
                  maxHeight: '100%',
                  objectFit: 'contain',
                }}
              />
              {dimensions && (
                <Box
                  sx={{
                    position: 'absolute',
                    bottom: 8,
                    right: 8,
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    color: '#fff',
                    padding: '.25rem .5rem',
                    borderRadius: '.25rem',
                    fontSize: '.75rem',
                    fontFamily: 'monospace',
                    zIndex: 1,
                  }}>
                  {dimensions.width} × {dimensions.height}
                </Box>
              )}
            </Box>
          ) : (
            <CanvasImage
              canvasRef={canvasRef}
              selectedImage={previewImage}
              setIsImageLoading={setIsImageLoading}
              selectedImageElement={previewImageElement}
              strokesLayer={strokesLayer}
              rotation={rotation}
            />
          )}
        </Box>

        <Box
          sx={{
            width: '100%',
            maxWidth: '100%',
            overflowX: 'auto',
            padding: '1rem',
            boxSizing: 'border-box',
            display: 'grid',
            gridAutoFlow: 'column',
            gridAutoColumns: '9rem',
            gap: '1rem',
            '&::-webkit-scrollbar': {
              height: '.5rem',
            },
            '&::-webkit-scrollbar-track': {
              background: '#f1f1f1',
              borderRadius: '.25rem',
            },
            '&::-webkit-scrollbar-thumb': {
              background: '#888',
              borderRadius: '.25rem',
              '&:hover': {
                background: '#555',
              },
            },
          }}
          onWheel={(e) => {
            e.preventDefault();
            const container = e.currentTarget;
            container.scrollLeft += e.deltaY;
          }}
          onClick={() => {
            setPreviewImage(null);
          }}>
          {images?.map((image_id: string, index: number) => {
            if (!imagesMapRef?.current?.has(image_id)) return;

            const image = imagesMapRef.current.get(image_id) as IImage;
            const {
              id,
              thumb: { src },
            } = image;
            const ratio =
              image.width && image.height ? image.width / image.height : 1;

            return (
              <Box
                key={id}
                sx={{
                  width: '9rem',
                  height: '9rem',
                }}>
                <CardActionArea
                  sx={{
                    position: 'relative',
                    height: '9rem',
                    border: '1px solid #eee',
                    borderRadius: '.25rem',
                  }}>
                  <img
                    src={src}
                    alt={`Thumbnail ${index + 1}`}
                    style={{
                      width: ratio < 1 ? `${ratio * 100}%` : '100%',
                      height: ratio < 1 ? '100%' : `${(1 / ratio) * 100}%`,
                      objectFit: 'contain',
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                      borderRadius: '.25rem',
                    }}
                  />
                  <StyledImageOverlay
                    sx={{
                      width: '100%',
                      height: '100%',
                      overflow: 'hidden',
                      display: 'flex',
                      gap: 1,
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      zIndex: 1,
                    }}>
                    <IconButton
                      onClick={(e) => {
                        e.stopPropagation();
                        setRotation(0);
                        image && setPreviewImage(image);
                      }}
                      size="small"
                      sx={{ color: 'white' }}>
                      <BorderColor />
                    </IconButton>
                    <IconButton
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedImage(selectedImage ? null : image);
                      }}
                      size="small"
                      sx={{ color: 'white' }}>
                      <Slideshow />
                    </IconButton>
                  </StyledImageOverlay>
                </CardActionArea>
              </Box>
            );
          })}
        </Box>
      </Box>
    </>
  );
};

export default AIRenderer;
